import React from "react";
import { Check, ArrowRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import SectionHeading from "@/components/common/SectionHeading";
import Icon from "@/components/common/Icon";
import { Reveal, Stagger, StaggerItem } from "@/components/common/Reveal";
import { CTAButton } from "@/components/common/CTA";
import FinalCTA from "@/components/sections/FinalCTA";

export default function ServicePageTemplate({
  seo,
  breadcrumbs,
  eyebrow,
  title,
  intro,
  leadCopy,
  image,
  imageAlt,
  bullets = [],
  subServices = [],
  note,
  children,
}) {
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: title,
    provider: { "@type": "Organization", name: "Premier Global Sourcing (Private) Limited" },
    areaServed: ["Pakistan", "China", "UAE", "Saudi Arabia", "Iran", "USA", "Europe"],
    description: seo?.description,
  };

  return (
    <>
      <SEO {...seo} jsonLd={serviceJsonLd} />
      <PageHero eyebrow={eyebrow} title={title} intro={intro} breadcrumbs={breadcrumbs}>
        <CTAButton to="/request-a-quote" variant="primary" icon={ArrowRight} data-testid="service-hero-cta">
          Request a sourcing quote
        </CTAButton>
      </PageHero>

      {/* Lead + image */}
      <section className="container-page py-20 lg:py-28 grid gap-12 lg:grid-cols-12 items-center">
        <Reveal className="lg:col-span-6">
          <p className="text-[19px] leading-[30px] text-navy font-medium">{leadCopy}</p>
          <ul className="mt-8 space-y-4">
            {bullets.map((b) => (
              <li key={b} className="flex gap-3 text-[15.5px] text-muted-foreground">
                <span className="h-6 w-6 rounded-full bg-[#EAF1F4] flex items-center justify-center shrink-0">
                  <Check className="h-3.5 w-3.5 text-teal" />
                </span>
                {b}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6">
          <div className="relative overflow-hidden rounded-lg border border-[#D9E0E4]">
            <img src={image} alt={imageAlt} width="1100" height="825" loading="lazy"
              className="w-full h-[300px] lg:h-[420px] object-cover" />
          </div>
        </Reveal>
      </section>

      {/* Sub-services */}
      {subServices.length > 0 && (
        <section className="bg-alt border-y border-[#D9E0E4]">
          <div className="container-page py-20 lg:py-28">
            <SectionHeading eyebrow="What we coordinate" title="Included in this service" />
            <Stagger className="mt-12 grid gap-px bg-[#D9E0E4] sm:grid-cols-2 lg:grid-cols-3 border border-[#D9E0E4] rounded-lg overflow-hidden">
              {subServices.map((s) => (
                <StaggerItem key={s.title}>
                  <div className="h-full bg-white p-7">
                    <div className="h-11 w-11 rounded-md bg-[#EAF1F4] flex items-center justify-center">
                      <Icon name={s.icon} className="h-5 w-5 text-teal" />
                    </div>
                    <h3 className="mt-5 font-heading font-bold text-[17px] text-navy">{s.title}</h3>
                    <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">{s.copy}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {note && (
        <section className="container-page py-14">
          <div className="rounded-lg border border-[#D9E0E4] bg-white p-6 lg:p-8 max-w-3xl">
            <p className="text-[14px] text-muted-foreground leading-relaxed">{note}</p>
          </div>
        </section>
      )}

      {children}

      <FinalCTA />
    </>
  );
}
